import React, { FC, memo, useCallback, useEffect, useState } from 'react';

import { FocusContext, setFocus, useFocusable } from '@noriginmedia/norigin-spatial-navigation';
import { DialogItem } from '@smart-tv/components/dialog';
import Typography from '@smart-tv/components/typography';
import testIds from '@smart-tv/e2e/shared/variables';
import useFavoritesEdit from '@smart-tv/hooks/use-favorites-edit';
import usePrevious from '@smart-tv/hooks/use-previous';
import { LiveTvChannel } from '@smart-tv/typings/content';
import { IS_BROWSER } from '@smart-tv/utils/is-browser';

import {
  CHANNELS_FOCUS_KEY,
  CHANNELS_SELECTED_FOCUS_KEY,
  UNSELECTED_ITEM_FOCUS_KEY
} from './favorites-edit/favorites-edit-helpers';
import styles from './favorites.module.scss';

export const FavoritesEditChannels: FC = () => {
  const { channels, editedFavoritesList, handleChannelToggle, lastToggledChannel } =
    useFavoritesEdit();

  const { ref: scrollingRef, focusKey } = useFocusable({
    focusKey: CHANNELS_FOCUS_KEY,
    focusable: editedFavoritesList?.sids?.length !== channels?.length,
    autoRestoreFocus: false
  });

  const [unselectedChannels, setUnselectedChannels] = useState<LiveTvChannel[]>([]);

  const prevUnselectedChannels = usePrevious(unselectedChannels);
  const shouldAutoFocusFirstItem = !prevUnselectedChannels || prevUnselectedChannels.length === 0;

  const channelRowSize = 81;
  // this aligns chosen and non-chosen channels in the scroll view
  const magicOffset = 12;

  const channelFocused = useCallback(
    ({ node }: { node: HTMLElement }) => {
      if (scrollingRef.current !== null) {
        // Use the node's own offsetTop (stable) rather than the scroll-relative y from the layout,
        // which would compound drift across successive focus moves.
        scrollingRef.current.scrollTo({
          top: node.offsetTop - scrollingRef.current.offsetTop - channelRowSize * 4 + magicOffset,
          behavior: 'auto'
        });
      }
    },
    [scrollingRef]
  );

  useEffect(() => {
    if (!channels) return;

    if (editedFavoritesList?.sids.length) {
      if (editedFavoritesList?.sids?.length === channels.length) {
        setFocus(CHANNELS_SELECTED_FOCUS_KEY);
      }
      return setUnselectedChannels(
        channels.filter((channel) => editedFavoritesList?.sids.indexOf(channel.sid) === -1)
      );
    }

    setUnselectedChannels(channels);
  }, [channels, editedFavoritesList?.sids]);

  useEffect(() => {
    if (
      !lastToggledChannel?.wasAdded ||
      !prevUnselectedChannels ||
      unselectedChannels.length === 0 ||
      unselectedChannels.some((channel) => channel.sid === lastToggledChannel.sid)
    ) {
      return;
    }

    const channelIndex = prevUnselectedChannels.findIndex(
      (channel) => channel.sid === lastToggledChannel.sid
    );
    if (channelIndex === -1) return;

    const nextFocusIndex = Math.min(channelIndex, unselectedChannels.length - 1);
    setFocus(UNSELECTED_ITEM_FOCUS_KEY + unselectedChannels[nextFocusIndex].sid);
  }, [lastToggledChannel, prevUnselectedChannels, unselectedChannels]);

  return (
    <FocusContext.Provider value={focusKey}>
      <div
        className={
          IS_BROWSER
            ? styles['favorites-edit-root--channels-browser']
            : styles['favorites-edit-root--channels-tv']
        }
        ref={scrollingRef}
        data-testid={testIds.favorites.edit.channels}
      >
        {unselectedChannels?.map((channel, index) => (
          <DialogItem
            key={channel.sid}
            isFocused={index === 0 && shouldAutoFocusFirstItem}
            hasBackground={true}
            hasIcon={true}
            focusKey={UNSELECTED_ITEM_FOCUS_KEY + channel.sid}
            onFocus={channelFocused}
            onEnterPress={() => handleChannelToggle(channel.sid)}
            onClick={() => handleChannelToggle(channel.sid)} // the onClick is added to support E2E tests
            dataTestId={testIds.favorites.edit.channelsItem}
          >
            <img
              className={styles['favorites-edit-root--logo']}
              src={channel.icon}
              alt={channel.name}
            />
            <Typography noWrap>{channel.name}</Typography>
          </DialogItem>
        ))}
      </div>
    </FocusContext.Provider>
  );
};

export default memo(FavoritesEditChannels);
